import React, { useRef, useEffect, useState } from "react";
import { Box, Typography } from "@mui/material";
import { Logger } from "../../tools/Logger";
import axios from "axios";

export const VideoQuic = ({
  ipAddresses,
  uavId,
}: {
  ipAddresses: Array<string>;
  uavId: string;
}) => {
  const imgRef = useRef<HTMLImageElement>(null);
  const transportRefs = [
    useRef<WebTransport | null>(null),
    useRef<WebTransport | null>(null),
  ];
  const datagramReaders = [
    useRef<ReadableStreamDefaultReader | null>(null),
    useRef<ReadableStreamDefaultReader | null>(null),
  ];
  const fragmentBuffers: Array<{ [key: string]: any }> = [{}, {}];
  const latestServerTimestampRef = useRef(0);
  const [showVideoDebugTag, setShowVideoDebugTag] = useState<boolean>(false);
  const [currentStream, setCurrentStream] = useState<string>("");
  const [isStalled, setIsStalled] = useState<boolean>(false);
  const TAG = "QUICDASH";
  const lastResponseTimes = [useRef<number>(0), useRef<number>(0)];
  const lastFrameTime = [useRef<number>(Date.now()), useRef<number>(Date.now())];
  const isConnecting = [useRef<boolean>(false), useRef<boolean>(false)];
  const pendingPromises = useRef<Promise<any>[]>([]);
  const lastCloseTime = [useRef<number>(0), useRef<number>(0)];
  const connectAbortControllers = [useRef<AbortController | null>(null), useRef<AbortController | null>(null)];
  const lastFailureTime = [useRef<number>(0), useRef<number>(0)];

  (window as any).__QUIC_IDLE_TIMEOUT = 30000;  // 30 seconds
  (window as any).__QUIC_MAX_STREAMS = 100; // arbitrary limit for concurrent streams

  const retryDelay = 60000; // 60s, this stop the quic constant retry causing backoff buildup
  const busyLoopDelay = 100; // arbitary 100ms delay to prevent busy loop
  const connectTimeout = 10000; // 10s timeout for connection attempts
  const heartbeatInterval = connectTimeout/5; // 5s interval had some dropped packets, so we use 2s
  const staleFrame = 5000;

  function isValidJPEG(data: Uint8Array) {
    const isStartOfImage = data[0] === 0xff && data[1] === 0xd8;
    const isEndOfImage = data[data.length - 2] === 0xff && data[data.length - 1] === 0xd9;
    return isStartOfImage && isEndOfImage;
  }

  function handleFragment(
    buffer: any,
    frameId: number,
    totalFragments: number,
    fragmentIndex: number,
    fragmentData: Uint8Array
  ) {
    if (!buffer[frameId]) {
      buffer[frameId] = {
        fragments: Array(totalFragments).fill(null),
        receivedCount: 0,
      };
    }
    const frameBuffer = buffer[frameId];
    if (!frameBuffer.fragments[fragmentIndex]) {
      frameBuffer.fragments[fragmentIndex] = fragmentData;
      frameBuffer.receivedCount += 1;
    }
    if (frameBuffer.receivedCount === totalFragments) {
      const fullFrame = new Uint8Array(
        frameBuffer.fragments.reduce(
          (acc: number, frag: Uint8Array) => acc + frag.length,
          0
        )
      );
      let offset = 0;
      for (const frag of frameBuffer.fragments) {
        fullFrame.set(frag, offset);
        offset += frag.length;
      }
      delete buffer[frameId];
      return fullFrame;
    }
    return null;
  }

  const trackPromise = <T,>(promise: Promise<T>): Promise<T> => {
    pendingPromises.current.push(promise);
    return promise.finally(() => {
      const index = pendingPromises.current.indexOf(promise);
      if (index !== -1) {
        pendingPromises.current.splice(index, 1);
      }
    });
  };

  const waitForPendingPromises = async (): Promise<void> => {
    if (pendingPromises.current.length > 0) {
      Logger.info(TAG, `Waiting for ${pendingPromises.current.length} pending operations to complete...`);
      try {
        await Promise.all(pendingPromises.current);
      } catch (error) {
        Logger.error(TAG, `Error waiting for pending promises: ${error}`);
      }
      Logger.info(TAG, `All pending operations completed`);
    }
  };

  const forceCloseTransport = async (index: number): Promise<void> => {
    const transport = transportRefs[index].current;
    if (!transport) return;
    
    Logger.info(TAG, `Force closing all resources for SIM ${index + 1}...`);
    
    try {
      const reader = datagramReaders[index].current;
      if (reader) {
        await reader.cancel("Force close").catch(() => {});
        datagramReaders[index].current = null;
        Logger.info(TAG, `Reader canceled for SIM ${index + 1}`);
      }
    } catch (e) {
      Logger.error(TAG, `Error canceling reader: ${e}`);
    }
    
    try {
      transport.close();
      Logger.info(TAG, `Transport closed for SIM ${index + 1}`);
    } catch (e) {
      Logger.error(TAG, `Error closing transport: ${e}`);
    }
    
    transportRefs[index].current = null;
    
    Object.keys(fragmentBuffers[index]).forEach(key => {
      delete fragmentBuffers[index][key];
    });
    fragmentBuffers[index] = {};
    
    Logger.info(TAG, `All resources closed for SIM ${index + 1}`);
    lastCloseTime[index].current = Date.now();
  };

  const axiosInstance = axios.create({
    timeout: 5000,
  });

  const checkHealth = async (index: number, abortSignal: AbortSignal): Promise<boolean> => {
    const url = `https://${ipAddresses[index]}:5002/health`;
    try {
      Logger.info(TAG, `Checking health for SIM ${index + 1} at ${ipAddresses[index]} on ${uavId}`);
      const response = await axiosInstance.get(url);
      if (response.status === 200) {
        Logger.info(TAG, `Health check passed for SIM ${index + 1} at ${ipAddresses[index]} on ${uavId}`);
        return true;
      } else {
        Logger.warn(TAG, `Health check failed for SIM ${index + 1} on ${uavId}.`);
        return false;
      }
    } catch (error) {
      if (abortSignal.aborted) return false;
      
      if (axios.isAxiosError(error) && error.code === "ECONNABORTED") {
        Logger.warn(TAG, `Health check timed out for SIM ${index + 1} on ${uavId}.`);
      } else {
        Logger.error(TAG, `Health check error for SIM ${index + 1} on ${uavId}: ${error}`);
      }
      return false;
    }
  };

  let isCleaningUp = false;

  const cleanup = async () => {
    isCleaningUp = true;
    Logger.info(TAG, `[PERF] Starting cleanup at ${Date.now()} for ${uavId}`);
    await waitForPendingPromises();
    Logger.info(TAG, `[PERF] Pending promises cleared at ${Date.now()} for ${uavId}. Cleaning up transports`);
    await Promise.all([
      forceCloseTransport(0),
      forceCloseTransport(1)
    ]);
    Logger.info(TAG, `[PERF] forceCloseTransport finished at ${Date.now()} for ${uavId}, cleanup complete`);
    isCleaningUp = false;
  };

  const startStream = async (index: number, abortSignal: AbortSignal) => {
    connectAbortControllers[index].current = new AbortController();
    const connectAbortSignal = connectAbortControllers[index].current!.signal;
    const streamPromise = (async () => {
      Logger.info(TAG, `[PERF] startStream(${index}) called at ${Date.now()} for ${uavId}`);

      while (isCleaningUp) {
        Logger.info(TAG, `[PERF] Waiting for cleanup before starting stream ${index} at ${Date.now()} for ${uavId}`);
        await new Promise(resolve => setTimeout(resolve, busyLoopDelay));
      }
      if (isConnecting[index].current) {
        Logger.info(TAG, `[PERF] Already connecting for SIM ${index + 1} at ${Date.now()} for ${uavId}`);
        return;
      }
      isConnecting[index].current = true;
      Logger.info(TAG, `[PERF] Forcing close of previous transport for SIM ${index + 1} at ${Date.now()} for ${uavId}`);
      await forceCloseTransport(index);
      await new Promise(resolve => setTimeout(resolve, busyLoopDelay));
      if (abortSignal.aborted) {
        Logger.info(TAG, `[PERF] Aborted before connection for SIM ${index + 1} at ${Date.now()} for ${uavId}`);
        isConnecting[index].current = false;
        return;
      }
      const fragmentBuffer = fragmentBuffers[index];
      let temporaryTransport: WebTransport | null = null;

      try {
        Logger.info(TAG, `[PERF] Health check starting for SIM ${index + 1} at ${Date.now()} for ${uavId}`);
        const healthStart = Date.now();
        const isHealthy = await checkHealth(index, abortSignal);
        Logger.info(TAG, `[PERF] Health check finished for SIM ${index + 1} at ${Date.now()} (duration: ${Date.now() - healthStart}ms) for ${uavId}`);
        if (abortSignal.aborted) {
          Logger.info(TAG, `[PERF] Aborted after health check for SIM ${index + 1} at ${Date.now()} for ${uavId}`);
          isConnecting[index].current = false;
          return;
        }
        if (!isHealthy) {
          Logger.warn(TAG, `[PERF] Health check failed for SIM ${index + 1} at ${Date.now()} for ${uavId}`);
          throw new Error(`Health check failed for SIM ${index + 1}`);
        }

        Logger.info(TAG, `[PERF] Starting WebTransport connection for SIM ${index + 1} at ${Date.now()} for ${uavId}`);
        const connectStart = Date.now();
        const timeoutPromise = new Promise<never>((_, reject) => {
          setTimeout(() => reject(new Error(`Connection timeout for SIM ${index + 1}`)), connectTimeout);
        });

        const url = `https://${ipAddresses[index]}:5001/wt?sim=${index + 1}&uavId=${uavId}`;
        Logger.info(TAG, `[PERF] WebTransport URL for SIM ${index + 1}: ${url}`);

        const transport = await Promise.race([
          (async () => {
            const startTime = performance.now();
            temporaryTransport = new WebTransport(
              url,
              {
                congestionControl: 'cubic',
                QUIC_IDLE_TIMEOUT_MILLISECONDS: 30000,
                requireUnreliable: true,
                allowPooling: false,
                serverTimeout: connectTimeout
              } as any
            );
            
            temporaryTransport.closed
              .then(() => Logger.info(TAG, `WebTransport closed for SIM ${index + 1} on ${uavId}`))
              .catch(error => Logger.error(TAG, `WebTransport closed with error for SIM ${index + 1} on ${uavId}: ${error}`));
            
            Logger.info(TAG, `[PERF] Waiting for WebTransport ready state for SIM ${index + 1} at ${Date.now()} for ${uavId}`);
            await Promise.race([
              temporaryTransport.ready,
              new Promise((_, reject) => {
                connectAbortSignal.addEventListener("abort", () => reject(new Error("Connection aborted by other SIM")), { once: true });
              })
            ]);
            const endTime = performance.now();

            if (abortSignal.aborted) {
              Logger.info(TAG, `[PERF] Aborted during WebTransport ready for SIM ${index + 1} at ${Date.now()} for ${uavId}`);
              temporaryTransport.close();
              return null;
            }

            Logger.info(
              TAG,
              `[PERF] WebTransport connection established in ${endTime - startTime}ms for SIM ${index + 1} at ${Date.now()} for ${uavId}`
            );

            return temporaryTransport;
          })(),
          timeoutPromise
        ]).catch(error => {
          if (temporaryTransport) {
            temporaryTransport.close();
            temporaryTransport = null;
          }
          Logger.error(TAG, `[PERF] WebTransport connection failed for SIM ${index + 1} at ${Date.now()} for ${uavId}: ${error}`);
          throw error;
        });

        const otherIndex = index === 0 ? 1 : 0;
        if (connectAbortControllers[otherIndex].current) {
          connectAbortControllers[otherIndex].current!.abort();
        }

        Logger.info(TAG, `[PERF] WebTransport connection finished for SIM ${index + 1} at ${Date.now()} (duration: ${Date.now() - connectStart}ms) for ${uavId}`);

        if (!transport) {
          throw new Error(`Transport initialization failed for SIM ${index + 1} on ${uavId}.`);
        }

        transportRefs[index].current = transport;
        lastFrameTime[index].current = Date.now();

        try {
          const heartbeat = {
            type: 'heartbeat',
            timestamp: Date.now(),
            sim: index + 1
          };
          const writer = transport.datagrams.writable.getWriter();
          await writer.write(new TextEncoder().encode(JSON.stringify(heartbeat)));
          writer.releaseLock();
          lastResponseTimes[index].current = Date.now();
          Logger.info(TAG, `Sent heartbeat for SIM ${index + 1} on ${uavId}`);
        } catch (error) {
          Logger.error(TAG, `Failed to send initial heartbeat for SIM ${index + 1}: ${error}`);
        }
        
        const datagramReader = transport.datagrams.readable.getReader();
        datagramReaders[index].current = datagramReader;
        
        while (!abortSignal.aborted) {
          const currentTime = Date.now();
      
          if (currentTime - lastFrameTime[index].current > connectTimeout) {
            Logger.warn(TAG, `No frames received for 10s on SIM ${index + 1} ${uavId}, closing connection`);
            throw new Error(`Frame timeout for SIM ${index + 1} on ${uavId}`);
          }

          if (currentTime - lastResponseTimes[index].current >= heartbeatInterval) {
            try {
              const heartbeat = {
                type: 'heartbeat',
                timestamp: currentTime,
                sim: index + 1
              };
              const writer = transport.datagrams.writable.getWriter();
              await writer.write(new TextEncoder().encode(JSON.stringify(heartbeat)));
              writer.releaseLock();
              lastResponseTimes[index].current = currentTime;
              Logger.info(TAG, `Sent heartbeat for SIM ${index + 1} on ${uavId}`);
            } catch (error) {
              Logger.error(TAG, `Failed to send heartbeat for SIM ${index + 1}: ${error}`);
              throw error;
            }
          }

          const { value, done } = await datagramReader.read();
          if (done || !value) {
            throw new Error(`Stream ${index + 1} disconnected on ${uavId}.`);
          }
          
          const dataView = new DataView(value.buffer);
          const frameId = dataView.getUint16(0, false);
          const totalFragments = dataView.getUint8(2);
          const fragmentIndex = dataView.getUint8(3);
          const fragmentLength = dataView.getUint16(4, false);
          const serverTimestamp = Number(dataView.getBigUint64(6, false));
          
          if (
            serverTimestamp < latestServerTimestampRef.current &&
            latestServerTimestampRef.current !== 0
          ) {
            continue;
          }
          
          latestServerTimestampRef.current = serverTimestamp;
          
          const fragmentData = new Uint8Array(value.buffer, 14, fragmentLength);
          const fullFrame = handleFragment(
            fragmentBuffer,
            frameId,
            totalFragments,
            fragmentIndex,
            fragmentData
          );
          
          if (!imgRef.current) {
            continue;
          }
          
          if (fullFrame && !isValidJPEG(fullFrame)) {
            Logger.error(TAG, `Full frame is not valid JPEG on ${uavId}`);
            continue;
          }

          if (fullFrame) {
            const clientTimestamp = Date.now();
            lastFrameTime[index].current = currentTime;
            const latency = clientTimestamp - serverTimestamp;
            const latencyThreshold = 3000; // arbitary
            if (latency > latencyThreshold) {
              Logger.error(TAG, `Latency ${latency} on ${uavId}`);
            }

            const blob = new Blob([fullFrame], { type: "image/jpeg" });
            const objectURL = URL.createObjectURL(blob);
            imgRef.current.src = objectURL;
            setCurrentStream(`SIM ${index + 1} (${ipAddresses[index]})`);
            setTimeout(() => URL.revokeObjectURL(objectURL), busyLoopDelay);
          }
        }
      } catch (error) {
        if (!abortSignal.aborted) {
          await forceCloseTransport(index);
          Logger.error(
            TAG,
            `WebTransport connection failed for SIM ${index + 1} on ${uavId}: ${error}`
          );
          isConnecting[index].current = false;

          lastFailureTime[index].current = Date.now();

          const otherIndex = index === 0 ? 1 : 0;
          if (
            !transportRefs[otherIndex].current &&
            !isConnecting[otherIndex].current
          ) {
            Logger.info(TAG, `Attempting to start SIM ${otherIndex + 1} after SIM ${index + 1} failure on ${uavId}`);
            startStream(otherIndex, abortSignal);
          }
          return;
        }
      } finally {
        if (abortSignal.aborted) {
          isConnecting[index].current = false;
        }
      }
    })();
    
    trackPromise(streamPromise);
  };

  useEffect(() => {
    const handleBeforeUnload = () => {
      Logger.info(TAG, `Page unloading, force closing all transports`);
      transportRefs.forEach((ref, index) => {
        if (ref.current) {
          try {
            ref.current.close();
          } catch (e) {
          }
        }
      });
    };
    
    window.addEventListener('beforeunload', handleBeforeUnload);
    
    return () => {
      window.removeEventListener('beforeunload', handleBeforeUnload);
    };
  }, []);

  useEffect(() => {
    const abortControllers = [new AbortController(), new AbortController()];
    Logger.info(
      TAG,
      `Video QUIC component starting for ${uavId} ${ipAddresses[0]} ${ipAddresses[1]}`
    );

    startStream(0, abortControllers[0].signal);
    startStream(1, abortControllers[1].signal);

    const interval = setInterval(async () => {
      for (let i = 0; i < ipAddresses.length; i++) {
        if (!isConnecting[i].current) {
          const healthy = await checkHealth(i, abortControllers[i].signal);
          if (healthy) {
            Logger.info(TAG, `Periodic health check passed for SIM ${i + 1} on ${uavId}`);
            return;
          }
          Logger.info(TAG, `Periodic health check failed for SIM ${i + 1} on ${uavId}`);
        }
      }
    }, retryDelay);

    return () => {
      Logger.info(TAG, `Cleaning up component for ${uavId}`);

      abortControllers.forEach(controller => {
        if (!controller.signal.aborted) {
          controller.abort();
        }
      });

      clearInterval(interval);

      waitForPendingPromises().then(() => cleanup());
    };
  }, []);

  useEffect(() => {
    const stallCheckInterval = setInterval(() => {
      const lastFrame = Math.max(lastFrameTime[0].current, lastFrameTime[1].current);
      setIsStalled(Date.now() - lastFrame > staleFrame);
    }, 1000);

    return () => clearInterval(stallCheckInterval);
  }, []);

  return (
    <Box
      sx={{
        width: "100%",
        height: 290,
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        position: "relative",
        backgroundColor: "#000",
      }}
    >
      <img
        ref={imgRef}
        width="100%"
        height="290"
        style={{ objectFit: "cover" }}
        alt="Video stream"
        onError={(e) => {
          Logger.error(TAG, `Image load error on ${uavId}: ${e.currentTarget.src}`);
        }}
      />
      {isStalled && showVideoDebugTag && (
        <Box
          sx={{
            position: "absolute",
            top: 12,
            right: 12,
            width: 16,
            height: 16,
            borderRadius: "50%",
            backgroundColor: "#FFC107",
            border: "2px solid white",
            zIndex: 10,
          }}
        />
      )}
      {showVideoDebugTag && (
          <Typography
          variant="caption"
          color="white"
          sx={{
            position: "absolute",
            bottom: 8,
            left: 8,
            backgroundColor: "rgba(0,0,0,0.6)",
            padding: "4px 8px",
            borderRadius: "4px",
          }}
        >
          {currentStream}
        </Typography>
      )}
    </Box>
  );
};
